import axios from "axios";
import { ArrowRight, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { HiChevronRight, HiInformationCircle } from "react-icons/hi2";
import { IoBagHandleOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import SubCategoryCard from "../../components/UserSideComponents/services/SubCategoryCard";
import Tabs from "../../components/UserSideComponents/services/Tabs";
import TopCard from "../../components/UserSideComponents/TopCard";
import { useCart } from "../../context/CartContext";

import UserServiceCart from "./UserServiceCart";

const UserServices = () => {
  const { id } = useParams();

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [activeTab, setActiveTab] = useState(null); // categoryId

  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSubCategories, setLoadingSubCategories] = useState(true);

  const transformCategories = (data = []) =>
    data.map((cat) => ({
      id: cat._id,
      name: cat.categoryName,
    }));

  const transformSubCategories = (subCategories = []) => {
    return subCategories.map((cat) => ({
      categoryId: cat?.categoryId?._id,
      categoryName: cat?.categoryId?.categoryName,
      subCategoryId: cat?._id,
      subCategoryName: cat.subCategoryName,
      description: cat?.description,
      imageUrl: cat?.imageUrl,
      totalServices: cat?.totalServices,
      rating: cat?.rating ?? 4.9,
    }));
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/service-category/get-all`,
      );

      if (res.data?.success) {
        const transformed = transformCategories(res.data.data);
        setCategories(transformed);

        if (id) setActiveTab(id);
        else if (transformed.length) setActiveTab(transformed[0].id);
      }
    } catch (err) {
      console.error("Category fetch failed", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-category/get-all`,
      );

      if (res.data?.success) {
        setSubCategories(transformSubCategories(res.data.data));
      }
    } catch (err) {
      console.error("Sub-category fetch failed", err);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) return;

    setActiveTab(id);
  }, [id]);
  
  const filteredSubCategories = subCategories.filter(
    (sub) => sub.categoryId === activeTab,
  );

  return (
    <div className="bg-brandAccentColor/15 pb-6 sm:pb-8 md:pb-10 lg:pb-12 ">
      <TopCard />

      <div className="flex flex-col px-4 sm:px-8 md:px-10 lg:px-12">
        {loadingCategories ? (
          <CategoryTabsSkeleton />
        ) : (
          <Tabs
            tabs={categories}
            loading={loadingCategories}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        )}

        <div className="flex gap-4 relative lg:gap-4 xl:gap-8 ">
          <div className="flex flex-col w-full lg:w-[70%] gap-y-2 sm:gap-y-4 py-2  ">
            {loadingSubCategories ? (
              <SubCategorySkeletonList count={4} />
            ) : filteredSubCategories.length === 0 ? (
              <EmptyState
                title="No services here yet."
                description="We’re preparing services for this category. Take a look at other categories while we set things up."
              />
            ) : (
              filteredSubCategories.map((sub) => (
                <SubCategoryCard key={sub.subCategoryId} subCategory={sub} />
              ))
            )}
          </div>

          {/* RIGHT SUMMARY */}
          <div className="hidden lg:block w-[30%] py-2">
            <div className="sticky top-32 w-full min-w-0 overflow-hidden">
              <UserServiceCart variant="panel" />
            </div>
          </div>
        </div>
      </div>
      <MobileCartBar />
    </div>
  );
};

const MobileCartBar = () => {
  const { mobileOpen, cart = [], totals } = useCart();
  const navigate = useNavigate();

  if (!cart.length || !mobileOpen) return null;

  const { totalQty, totalPrice, isMinOrderReached, remainingToMinOrder } =
    totals;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 lg:hidden">
      {/* MIN ORDER INFO STRIP */}
      {!isMinOrderReached && (
        <div
          className="
      bg-gradient-to-r from-orange-50 to-amber-50
      border-t border-orange-200/60
      px-4 py-2
      text-center
      backdrop-blur-sm
    "
        >
          <p className="text-[11px] font-medium text-orange-800 flex items-center justify-center gap-1.5">
            <HiInformationCircle className="w-3.5 h-3.5 flex-shrink-0 text-orange-600" />
            Add{" "}
            <span className="font-bold text-orange-900 tabular-nums">
              ₹{remainingToMinOrder}
            </span>{" "}
            more to reach minimum order
          </p>
        </div>
      )}

      {/* MAIN CART BAR */}
      <div
        className="
        bg-white
        border-t border-gray-200
        px-4 py-2.5
        shadow-[0_-4px_20px_rgba(0,0,0,0.08)]
        backdrop-blur-md
        bg-opacity-98
      "
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          {/* Left: Cart Summary */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Cart Icon Badge */}
            <div className="relative flex-shrink-0">
              <div
                className="
          w-11 h-11
          rounded-xl
  bg-gradient-to-r from-[#f0653a] to-[#e4572f]
            flex items-center justify-center
          shadow-md shadow-orange-500/30
        "
              >
                <IoBagHandleOutline className="w-6 h-6 text-white" />
              </div>
              {totalQty > 0 && (
                <div
                  className="
            absolute -top-1.5 -right-1.5
            min-w-[18px] h-[18px]
            bg-red-500
            text-white
            text-[10px] font-bold
            rounded-full
            flex items-center justify-center
            shadow-md
            border-2 border-white
            tabular-nums
          "
                >
                  {totalQty > 99 ? "99+" : totalQty}
                </div>
              )}
            </div>

            {/* Text Info */}
            <div className="flex flex-col min-w-0">
              <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                {totalQty} service{totalQty !== 1 ? "s" : ""}
              </span>
              <span className="text-lg font-bold text-gray-900 tabular-nums tracking-tight">
                ₹{totalPrice.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          {/* Right: CTA Button */}
          <button
            onClick={() => navigate("/service-cart")}
            disabled={!isMinOrderReached}
            className={`
    relative
    flex items-center justify-center gap-2
    px-5 py-2.5
    rounded-xl
    text-sm font-bold
    transition-all duration-200
    overflow-hidden
    flex-shrink-0

    ${
      isMinOrderReached
        ? `
          bg-gradient-to-r from-[#f0653a] to-[#e4572f]
          text-white
          hover:from-[#e4572f] hover:to-[#d94d28]

          shadow-md shadow-[#f0653a]/30
          hover:shadow-xl hover:shadow-[#f0653a]/50

          active:scale-[0.96]
        `
        : `
          bg-gray-200
          text-gray-400
          cursor-not-allowed
          shadow-none
        `
    }
  `}
          >
            {/* Shimmer effect for enabled state */}
            {isMinOrderReached && (
              <div
                className="
          absolute inset-0
          bg-gradient-to-r from-transparent via-white/20 to-transparent
          -translate-x-full
          animate-[shimmer_2s_infinite]
        "
                style={{
                  animation: "shimmer 2s infinite",
                }}
              />
            )}

            <span className="relative z-10 whitespace-nowrap">View Cart</span>

            <HiChevronRight className="w-4 h-4 relative z-10" />
          </button>
        </div>
      </div>

      {/* Add shimmer animation */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};

const CategoryTabsSkeleton = () => {
  return (
    <div className="flex gap-2 md:gap-3 py-3 overflow-x-hidden animate-pulse">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-7 sm:h-9 w-20 sm:w-24 rounded-full bg-gray-200"
        />
      ))}
    </div>
  );
};

const SubCategorySkeletonList = ({ count = 4 }) => {
  return (
    <div className="flex flex-col gap-y-4 py-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg p-4 lg:p-5 animate-pulse">
          <div className="flex gap-3 xl:gap-4">
            {/* Image */}
            <div className="size-[5.5rem] sm:size-24 md:size-28 lg:size-32 xl:size-36 bg-gray-200 rounded-lg" />

            {/* Content */}
            <div className="flex flex-1 flex-col lg:flex-row lg:justify-between gap-2 sm:py-1">
              <div className="flex flex-col gap-y-1 md:gap-y-1.5 lg:gap-y-2">
                <div className="h-4 w-24 bg-gray-200 rounded" />
                <div className="h-5 w-40 bg-gray-200 rounded" />
                <div className="h-4 w-56 bg-gray-200 rounded" />
              </div>

              <div className="flex gap-2 lg:flex-col lg:items-end">
                <div className="h-4 w-16 bg-gray-200 rounded" />
                <div className="h-4 w-32 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const EmptyState = ({
  title = "Good things take a moment ✨",
  description = "Services for this category are being curated. Meanwhile, feel free to explore other options.",
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {/* Icon */}
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-brandAccentColor/20 mb-5">
        <Sparkles className="w-8 h-8 text-accentTextColor" />
      </div>

      {/* Title */}
      <h3 className="text-base sm:text-lg font-semibold text-primaryTextColor">
        {title}
      </h3>

      {/* Description */}
      <p className="mt-2 text-sm text-secondaryTextColor max-w-xs">
        {description}
      </p>

      {/* Hint */}
      <div className="flex items-center gap-1 mt-4 text-xs text-accentTextColor opacity-80">
        <ArrowRight className="w-3 h-3" />
        Try another category above
      </div>
    </div>
  );
};

export default UserServices;
